import type { PlayerListItem } from '#shared/schemas/player';

/**
 * Name search that treats Greek and Latin as one alphabet. The crew types names either way:
 * "nikos" has to find Νίκος, "ΕΛΕΝΗ" has to find Ελένη, and accents never matter. Both sides go
 * through `fold`, which lowercases, drops accents and maps Greek letters to their usual Latin
 * spelling (the way a Greek would type it without switching keyboards).
 */

const GREEK: Record<string, string> = {
  α: 'a',
  β: 'v',
  γ: 'g',
  δ: 'd',
  ε: 'e',
  ζ: 'z',
  η: 'i',
  θ: 'th',
  ι: 'i',
  κ: 'k',
  λ: 'l',
  μ: 'm',
  ν: 'n',
  ξ: 'x',
  ο: 'o',
  π: 'p',
  ρ: 'r',
  σ: 's',
  ς: 's',
  τ: 't',
  υ: 'i',
  φ: 'f',
  χ: 'ch',
  ψ: 'ps',
  ω: 'o',
};

/** Lowercase, no accents, Greek letters in Latin. "Δημήτρης" and "dimitris" fold to the same text. */
export function fold(input: string): string {
  const base = input
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    // ου is one sound, "u"; keep it before the letter by letter pass turns υ into i.
    .replace(/ου/g, 'u');
  let out = '';
  for (const ch of base) out += GREEK[ch] ?? ch;
  // Latin y and Greek υ are the same sound for this purpose.
  return out.replace(/y/g, 'i');
}

/** Every word you typed must appear in the name or nickname, in any order. An empty query matches all. */
export function matchPlayer(p: Pick<PlayerListItem, 'displayName' | 'nickname'>, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = fold(`${p.displayName} ${p.nickname ?? ''}`);
  return words.every((w) => hay.includes(w));
}
